import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import BudgetV2UploadDialog from "@/components/BudgetV2UploadDialog";
import HeadReviewDialog from "@/components/HeadReviewDialog";
import BudgetTemplateDownloads from "@/components/BudgetTemplateDownloads";
import WorkingDraftsPanel from "@/components/WorkingDraftsPanel";
import { Upload, Landmark, TrendingDown, TrendingUp, Wrench } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { canUploadSanctioned, canViewSanctioned } from "@/lib/roles";

const rupee = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

/**
 * Phase-2 Budget V2 page.
 * Left: project picker. Right: sanctioned + working upload buttons, and a
 * live Category → Head → Sub-Type tree with sanctioned / working / balance.
 */
export default function BudgetV2() {
  const { user } = useAuth();
  const location = useLocation();
  const retiredNotice = location.state?.retiredNotice;
  const [projects, setProjects] = useState([]);
  const [pid, setPid] = useState("");
  const [tree, setTree] = useState(null);
  const [dialog, setDialog] = useState({ open: false, kind: "sanctioned" });

  // Query-param handoff from /approvals/pending. When the Authorizer clicks
  // a working-draft row we land here with ?project_id=X&approve_draft=Y —
  // preselect the project and pop the review dialog for that draft.
  const searchParams = new URLSearchParams(location.search);
  const urlProjectId = searchParams.get("project_id") || "";
  const urlApproveDraftId = searchParams.get("approve_draft") || "";

  const canSanctioned = user && canUploadSanctioned(user.role);
  const showSanc = user && canViewSanctioned(user.role);
  const canWorking = user && ["Admin", "Accounts", "Approver", "Authorizer"].includes(user.role);

  useEffect(() => {
    if (retiredNotice) toast.info(retiredNotice, { id: "legacy-budget-retired", duration: 8000 });
  }, [retiredNotice]);

  useEffect(() => {
    api.get("/projects").then(r => {
      setProjects(r.data.filter(p => p.project_code));
      // Prefer the project passed in via URL (approvals inbox deep-link),
      // otherwise the first project the user is entitled to.
      const preferred = urlProjectId || r.data.find(p => p.project_code)?.id || "";
      if (preferred && !pid) setPid(preferred);
    });
  }, [urlProjectId]);

  const loadTree = () => {
    if (!pid) return;
    api.get(`/projects/${pid}/budget-tree`).then(r => setTree(r.data));
  };
  useEffect(() => { loadTree(); }, [pid]);

  const currentProject = projects.find(p => p.id === pid);

  return (
    <div className="space-y-4 p-4 md:p-6" data-testid="budget-v2-page">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Landmark className="w-6 h-6" /> Budget (Codified)
          </h1>
          <p className="text-sm text-slate-500 mt-1">Category → Head → Sub-Type → Allocation · sanctioned & working uploads</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={pid} onValueChange={setPid}>
            <SelectTrigger className="w-[280px]" data-testid="v2-project-picker"><SelectValue placeholder="Select project" /></SelectTrigger>
            <SelectContent>
              {projects.map(p => (
                <SelectItem key={p.id} value={p.id}>{p.project_code} · {p.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {canSanctioned && <Button onClick={() => setDialog({ open: true, kind: "sanctioned" })} disabled={!pid} data-testid="v2-open-sanc">
            <Upload className="w-4 h-4 mr-1" /> Sanctioned
          </Button>}
          {canWorking && <Button variant="outline" onClick={() => setDialog({ open: true, kind: "working" })} disabled={!pid} data-testid="v2-open-work">
            <Wrench className="w-4 h-4 mr-1" /> Working
          </Button>}
        </div>
      </div>

      {pid && (
        <BudgetTemplateDownloads projectId={pid} projectCode={currentProject?.project_code || ""} userRole={user?.role} />
      )}

      {pid && (
        <WorkingDraftsPanel projectId={pid} userRole={user?.role} openDraftId={urlApproveDraftId} onApproved={loadTree} />
      )}

      {tree && (
        <div className="space-y-3">
          {tree.categories.filter(c => c.heads.length).map(c => (
            <Card key={c.code} data-testid={`v2-cat-${c.code}`}>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Badge variant="outline">{c.code}</Badge>
                  {c.name}
                  {c.max_percent && <span className="text-xs text-slate-500 font-normal">cap {c.max_percent}%</span>}
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                {c.heads.map(h => {
                  const hSanc = h.subtypes.reduce((s, x) => s + (x.sanctioned_amount || 0), 0);
                  const hWork = h.subtypes.reduce((s, x) => s + (x.working_amount || 0), 0);
                  const hBal = h.subtypes.reduce((s, x) => s + (x.balance_amount || 0), 0);
                  return (
                    <div key={h.id} className="mb-3">
                      <div className="flex items-center gap-3 text-sm font-medium border-b pb-1 mb-1">
                        <span className="font-mono text-xs">{h.code}</span>
                        <span>{h.name}</span>
                        <span className="ml-auto flex gap-4 text-xs font-normal">
                          {showSanc && <span>Sanctioned <b className="font-mono">{rupee(hSanc)}</b></span>}
                          <span>Working <b className="font-mono">{rupee(hWork)}</b></span>
                          <span>Balance <b className="font-mono">{rupee(hBal)}</b></span>
                        </span>
                      </div>
                      <table className="w-full text-xs">
                        <thead className="text-slate-500">
                          <tr>
                            <th className="text-left px-2 py-1">Code</th>
                            <th className="text-left px-2 py-1">Name</th>
                            {showSanc && <th className="text-right px-2 py-1">Sanctioned</th>}
                            <th className="text-right px-2 py-1">Working</th>
                            <th className="text-right px-2 py-1">Spent</th>
                            <th className="text-right px-2 py-1">Balance</th>
                            <th className="text-left px-2 py-1">Periods</th>
                          </tr>
                        </thead>
                        <tbody>
                          {h.subtypes.map(s => {
                            const diff = (s.working_amount || 0) - (s.sanctioned_amount || 0);
                            const trend = diff === 0 ? null : (diff > 0 ? <TrendingUp className="w-3 h-3 text-amber-600 inline" /> : <TrendingDown className="w-3 h-3 text-blue-600 inline" />);
                            return (
                              <tr key={s.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`v2-sub-${s.code}`}>
                                <td className="px-2 py-1 font-mono">{s.code}</td>
                                <td className="px-2 py-1">{s.name}</td>
                                {showSanc && <td className="px-2 py-1 text-right font-mono">{rupee(s.sanctioned_amount)}</td>}
                                <td className="px-2 py-1 text-right font-mono">{trend} {rupee(s.working_amount)}</td>
                                <td className="px-2 py-1 text-right font-mono text-slate-500">{rupee(s.spent_amount)}</td>
                                <td className="px-2 py-1 text-right font-mono font-medium">{rupee(s.balance_amount)}</td>
                                <td className="px-2 py-1 text-slate-500">{(s.allocations || []).map(a => `${a.period_label}=${rupee(a.working_amount || a.sanctioned_amount)}`).join(" · ")}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          ))}
          {tree.categories.every(c => c.heads.length === 0) && (
            <Card className="border-dashed">
              <CardContent className="p-8 text-center text-sm text-slate-500">
                No budget uploaded yet for this project. Click <b>Sanctioned</b> to upload the sanctioned CSV.
              </CardContent>
            </Card>
          )}
        </div>
      )}

      <BudgetV2UploadDialog
        kind={dialog.kind}
        projectId={pid}
        projectCode={currentProject?.project_code || ""}
        projectName={currentProject?.name || ""}
        open={dialog.open}
        onOpenChange={(v) => setDialog({ ...dialog, open: v })}
        onSuccess={loadTree}
      />
    </div>
  );
}
